import { api, cacheMinTimer, thunkLoader, thunks } from "@app/api";
import { call, createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployDiskAttachment,
  DeployDiskAttachmentResponse,
} from "@app/types";
import type { DeployOperationResponse } from "../operation";

export const defaultDiskAttachmentResponse = (
  d: Partial<DeployDiskAttachmentResponse> = {},
): DeployDiskAttachmentResponse => {
  const now = new Date().toISOString();
  return {
    id: "",
    mount_point: "",
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _type: "disk_attachment",
    ...d,
    _links: {
      persistent_disk: defaultHalHref(),
      service: defaultHalHref(),
      ...d._links,
    },
  };
};

export const deserializeDiskAttachment = (
  payload: DeployDiskAttachmentResponse,
): DeployDiskAttachment => {
  return {
    id: `${payload.id}`,
    mountPoint: payload.mount_point,
    // Prefer the API-emitted status (deploy-api/#2202); fall back to deriving
    // from `deleted_at` for builds that predate it.
    status:
      payload.status ?? (payload.deleted_at ? "deprovisioned" : "attached"),
    persistentDiskId: extractIdFromLink(payload._links.persistent_disk),
    serviceId: extractIdFromLink(payload._links.service),
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

export const hasDeployDiskAttachment = (a: DeployDiskAttachment) => a.id !== "";

export const selectDiskAttachmentById = schema.diskAttachments.selectById;
export const selectDiskAttachments = schema.diskAttachments.selectTable;
export const selectDiskAttachmentsAsList =
  schema.diskAttachments.selectTableAsList;
export const findDiskAttachmentById = schema.diskAttachments.findById;

export const findDiskAttachmentsByPersistentDiskId = (
  attachments: DeployDiskAttachment[],
  persistentDiskId: string,
) => attachments.filter((a) => a.persistentDiskId === persistentDiskId);

export const selectDiskAttachmentsByPersistentDiskId = createSelector(
  selectDiskAttachmentsAsList,
  (_: WebState, p: { persistentDiskId: string }) => p.persistentDiskId,
  findDiskAttachmentsByPersistentDiskId,
);

export const selectDiskAttachmentsByServiceId = createSelector(
  selectDiskAttachmentsAsList,
  (_: WebState, p: { serviceId: string }) => p.serviceId,
  (attachments, serviceId) =>
    attachments.filter((a) => a.serviceId === serviceId),
);

// The trimmed/lowercased mount points of a service's active (non-deprovisioned)
// attachments, used to enforce mount-point uniqueness when attaching a disk.
export const selectActiveMountPointsByServiceId = createSelector(
  selectDiskAttachmentsByServiceId,
  (attachments) =>
    attachments
      .filter((a) => a.status !== "deprovisioned")
      .map((a) => a.mountPoint.trim().toLowerCase()),
);

/**
 * Read-only "Disk Storage" indicator label for a service, shared by the
 * Services tables and the Service detail header:
 *   - 0 active attachments  -> null (callers render "N/A")
 *   - exactly 1 attachment  -> the disk handle (falls back to the disk id when
 *                              the disk record isn't hydrated yet)
 *   - 2+ attachments        -> "{n} Attached"
 * "Active" excludes deprovisioned attachments.
 */
export const diskStorageLabel = (
  attachments: DeployDiskAttachment[],
  disksById: Map<string, { id: string; handle: string }>,
): string | null => {
  const active = attachments.filter((a) => a.status !== "deprovisioned");
  if (active.length === 0) {
    return null;
  }
  if (active.length === 1) {
    const a = active[0];
    return disksById.get(a.persistentDiskId)?.handle ?? a.persistentDiskId;
  }
  return `${active.length} Attached`;
};

// The per-service "Disk Storage" label, reading the service's attachments and
// the environment's persistent disks straight from the store. Returns null when
// the service has no active attachment (callers render "N/A").
export const selectDiskStorageLabelByServiceId = createSelector(
  selectDiskAttachmentsByServiceId,
  (s: WebState) => schema.persistentDisks.selectTableAsList(s),
  (attachments, disks) =>
    diskStorageLabel(attachments, new Map(disks.map((d) => [d.id, d]))),
);

// Two-line display variant of the same selector. Returns null for the
// 0-attachment and 2+-attachment cases; callers fall back to the single-line
// label in those cases.
export const selectDiskStorageDisplayByServiceId = createSelector(
  selectDiskAttachmentsByServiceId,
  (s: WebState) => schema.persistentDisks.selectTableAsList(s),
  (attachments, disks) => {
    const active = attachments.filter((a) => a.status !== "deprovisioned");
    if (active.length !== 1) return null;
    const disksById = new Map(disks.map((d) => [d.id, d]));
    return disksById.get(active[0].persistentDiskId) ?? null;
  },
);

// Total active persistent-disk GB per service, keyed by service ID. Sort key
// for the Disk Storage column.
export const selectDiskStorageGbByServiceId = createSelector(
  selectDiskAttachmentsAsList,
  (s: WebState) => schema.persistentDisks.selectTableAsList(s),
  (attachments, disks) => {
    const disksById = new Map(disks.map((d) => [d.id, d]));
    const result: Record<string, number> = {};
    for (const a of attachments) {
      if (a.status === "deprovisioned") continue;
      const size = disksById.get(a.persistentDiskId)?.size ?? 0;
      result[a.serviceId] = (result[a.serviceId] ?? 0) + size;
    }
    return result;
  },
);

export const fetchDiskAttachments = api.get(
  "/disk_attachments?per_page=5000",
  {
    supervisor: cacheMinTimer(),
  },
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    yield* schema.update(schema.diskAttachments.reset());
  },
);

export const fetchDiskAttachment = api.get<{ id: string }>(
  "/disk_attachments/:id",
);

// Convenience selector pre-grouping attachments by disk for table rendering.
export const selectDiskAttachmentsByEnvId = createSelector(
  selectDiskAttachmentsAsList,
  (s: WebState) => schema.persistentDisks.selectTableAsList(s),
  (_: WebState, p: { envId: string }) => p.envId,
  (attachments, disks, envId) => {
    const diskIds = new Set(
      disks.filter((d) => d.environmentId === envId).map((d) => d.id),
    );
    return attachments.filter((a) => diskIds.has(a.persistentDiskId));
  },
);

interface DeprovisionDiskAttachmentOpProps {
  id: string;
  type: "deprovision";
}

export type DiskAttachmentOpProps = DeprovisionDiskAttachmentOpProps;

/**
 * Builds the snake_case operation request body for a disk_attachment operation.
 */
export const diskAttachmentOperationBody = (
  payload: DiskAttachmentOpProps,
): Record<string, string | number> => ({ type: payload.type });

// Detach is a `deprovision` operation created against the DiskAttachment
// itself (not the PersistentDisk).
export const createDiskAttachmentOperation = api.post<
  DiskAttachmentOpProps,
  DeployOperationResponse
>("/disk_attachments/:id/operations", function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(diskAttachmentOperationBody(ctx.payload)),
  });
  yield* next();
});

export interface DeprovisionDiskAttachmentProps {
  id: string;
}

/**
 * Detaches a disk from its service by creating a `deprovision` operation on
 * the DiskAttachment. The UI polls the returned operation to track progress.
 *
 * `thunkLoader` publishes the name-keyed loader state that
 * `useLoader(deprovisionDiskAttachment)` watches; failures are thrown so the
 * loader records the error message.
 */
export const deprovisionDiskAttachment =
  thunks.create<DeprovisionDiskAttachmentProps>("disk-attachment-deprovision", [
    thunkLoader,
    function* (ctx, next) {
      const opCtx = yield* call(() =>
        createDiskAttachmentOperation.run(
          createDiskAttachmentOperation({
            id: ctx.payload.id,
            type: "deprovision",
          }),
        ),
      );

      if (!opCtx.json.ok) {
        const message = opCtx.json.error.message;
        ctx.json = { error: message, opId: "" };
        throw new Error(message);
      }

      const opId = `${opCtx.json.value.id}`;
      ctx.json = { error: "", opId };
      ctx.loader = { meta: { opId } };
      yield* next();
    },
  ]);

export const diskAttachmentEntities = {
  disk_attachment: defaultEntity({
    id: "disk_attachment",
    deserialize: deserializeDiskAttachment,
    save: schema.diskAttachments.add,
  }),
};
